import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { users as usersApi } from '../services/api';
import { useMentionSubscription } from '../services/notifications';
import { playNotificationSound } from '../utils/notificationSound';

interface NotificationSoundContextValue {
  /** Whether the sound is enabled for the current user. */
  enabled: boolean;
  /** Update the preference (persists to server and localStorage). */
  setEnabled: (next: boolean) => void;
  /** Play the sound once, ignoring the enabled flag — for the "test" button. */
  playTest: () => void;
}

const NotificationSoundContext = createContext<NotificationSoundContextValue | undefined>(undefined);

const STORAGE_KEY = 'atlas-notification-sound-enabled';

/** Read the cached preference; defaults to enabled when unset. */
function readCachedEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'false') return false;
  if (stored === 'true') return true;
  return true;
}

export function NotificationSoundProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [enabled, setEnabledState] = useState<boolean>(readCachedEnabled);
  const enabledRef = useRef(enabled);
  const syncedUserRef = useRef<string | null>(null);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // Persist locally on every change so the next page load is correct even
  // before the server sync resolves.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
  }, [enabled]);

  // Reconcile with the server once per authenticated user, same pattern as
  // ThemeSync: adopt the server value if present, otherwise backfill from the
  // local cache.
  useEffect(() => {
    const currentUser = user?.userId ?? null;
    if (!currentUser) {
      syncedUserRef.current = null;
      return;
    }
    if (syncedUserRef.current === currentUser) return;
    syncedUserRef.current = currentUser;

    let cancelled = false;
    (async () => {
      try {
        const result = await usersApi.getNotificationSound();
        if (cancelled) return;
        if (typeof result.enabled === 'boolean') {
          if (result.enabled !== enabledRef.current) {
            setEnabledState(result.enabled);
          }
        } else {
          await usersApi.setNotificationSound(enabledRef.current);
        }
      } catch (err) {
        console.warn('Notification sound sync failed', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const setEnabled = useCallback((next: boolean) => {
    setEnabledState(next);
    usersApi.setNotificationSound(next).catch((err) => {
      console.warn('Failed to persist notification sound preference', err);
    });
  }, []);

  // Play on every incoming mention, gated by the current preference. Using a
  // ref so the subscription doesn't tear down on every toggle.
  useMentionSubscription(
    useCallback(() => {
      if (enabledRef.current) {
        playNotificationSound();
      }
    }, []),
  );

  const value = useMemo<NotificationSoundContextValue>(
    () => ({
      enabled,
      setEnabled,
      playTest: playNotificationSound,
    }),
    [enabled, setEnabled],
  );

  return (
    <NotificationSoundContext.Provider value={value}>
      {children}
    </NotificationSoundContext.Provider>
  );
}

export function useNotificationSound(): NotificationSoundContextValue {
  const ctx = useContext(NotificationSoundContext);
  if (!ctx) {
    throw new Error('useNotificationSound must be used within a NotificationSoundProvider');
  }
  return ctx;
}
